import { MatrixClient } from 'matrix-js-sdk/src/matrix';

import { DeferredInviteSnapshot } from './deferredInviteSnapshot';

export const DEFER_INVITE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const SESSION_KEY_PREFIX = 'meedio:defer-invite';
const sessionStorageKey = (userId: string) => `${SESSION_KEY_PREFIX}:${userId}:state`;

export type DeferInviteSessionState = {
  deferStartedAt: number;
  baselineRoomIds: string[];
  snapshots: DeferredInviteSnapshot[];
};

export type FlushEligibilityParams = {
  notificationId: string;
  inviteTimestamp: number;
  trackedAt: number;
  isNotificationRead: (id: string) => boolean;
};

export class DeferInviteSession {
  private state: DeferInviteSessionState | null = null;

  /** Preserved after endDeferral so flush hooks can still filter by defer window. */
  private lastDeferStartedAt: number | null = null;

  private lastBaselineRoomIds: string[] = [];

  get isActive(): boolean {
    return this.state !== null;
  }

  get deferStartedAt(): number | null {
    return this.state?.deferStartedAt ?? this.lastDeferStartedAt;
  }

  beginDeferral(matrixClient: MatrixClient): void {
    const roomIds = matrixClient.getRooms().map((room) => room.roomId);

    this.lastDeferStartedAt = null;
    this.lastBaselineRoomIds = [];
    this.state = {
      deferStartedAt: Date.now(),
      baselineRoomIds: roomIds,
      snapshots: [],
    };
  }

  endDeferral(): DeferInviteSessionState {
    const result = this.state ?? { deferStartedAt: 0, baselineRoomIds: [], snapshots: [] };
    if (this.state) {
      this.lastDeferStartedAt = this.state.deferStartedAt;
      this.lastBaselineRoomIds = this.state.baselineRoomIds;
    }
    this.state = null;
    return result;
  }

  trackSnapshot(snapshot: DeferredInviteSnapshot): void {
    if (!this.state) return;
    const exists = this.state.snapshots.some((s) => s.roomId === snapshot.roomId);
    if (!exists) this.state.snapshots.push(snapshot);
  }

  isEligibleForFlush(params: FlushEligibilityParams): boolean {
    const deferStartedAt = this.deferStartedAt;
    if (deferStartedAt == null && params.trackedAt === 0) return false;

    const sessionStart = deferStartedAt ?? 0;

    const isWithinSession = params.trackedAt >= sessionStart || params.inviteTimestamp >= sessionStart;
    if (!isWithinSession) return false;

    const isWithinMaxAge = Date.now() - params.inviteTimestamp < DEFER_INVITE_MAX_AGE_MS;
    if (!isWithinMaxAge) return false;

    // #8279: a room-scoped notification ID can already be "read" from a previously declined
    // invite. For invites that arrived during the defer session the read flag is stale, so
    // only apply the read gate to invites that predate the session.
    const isNewInvite = params.inviteTimestamp >= sessionStart;
    if (!isNewInvite && params.isNotificationRead(params.notificationId)) return false;

    return true;
  }

  isRoomInBaseline(roomId: string): boolean {
    const baseline = this.state?.baselineRoomIds ?? this.lastBaselineRoomIds;
    return baseline.includes(roomId);
  }

  hydrateFromSessionStorage(userId: string): void {
    try {
      const raw = sessionStorage.getItem(sessionStorageKey(userId));
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;

      const candidate = parsed as Record<string, unknown>;
      if (typeof candidate.deferStartedAt !== 'number') return;
      if (!Array.isArray(candidate.baselineRoomIds)) return;
      if (!Array.isArray(candidate.snapshots)) return;

      this.state = {
        deferStartedAt: candidate.deferStartedAt,
        baselineRoomIds: candidate.baselineRoomIds as string[],
        snapshots: candidate.snapshots as DeferredInviteSnapshot[],
      };
    } catch {
      // corrupted storage — start fresh
    }
  }

  persistToSessionStorage(userId: string): void {
    if (!this.state) return;

    try {
      sessionStorage.setItem(sessionStorageKey(userId), JSON.stringify(this.state));
    } catch {
      // storage full or unavailable
    }
  }

  clearOnLogout(userId: string): void {
    this.state = null;
    this.lastDeferStartedAt = null;
    this.lastBaselineRoomIds = [];
    try {
      sessionStorage.removeItem(sessionStorageKey(userId));
    } catch {
      // storage unavailable
    }
  }
}
